import { useEffect, useLayoutEffect, useState } from 'react'
import { useCurrentCanvasSide } from '../EditorDataProvider'
import { ArtworkWidget } from './ArtworkWidget'
import { colorInnerSvgPaths } from './Widgets.utils'
import {
  EDITOR_CONFIG_FACTOR,
  calculateAssetSize,
  calculatePatternWidgetStartPosition,
  getMargins,
  initialMultiColorAssetUrl,
} from '../editor.config'

export type TPatternWidgetVariant = 'one line pattern' | 'tile pattern'

export interface IPatternWidget {
  assetSvgData: string
  assetUrl?: string
  offset: boolean
  rotationAngle: number
  color: string
  variant?: TPatternWidgetVariant
  horizontalPositionStepCount: number
  verticalPositionStepCount: number
  scaleStepCount: number
  horizontalMarginStepCount: number
  verticalMarginStepCount: number
  multiColorMode?: boolean
}

export function PatternWidget({
  assetSvgData,
  assetUrl,
  offset,
  color,
  rotationAngle,
  verticalPositionStepCount,
  horizontalPositionStepCount,
  scaleStepCount,
  horizontalMarginStepCount,
  verticalMarginStepCount,
  variant,
  multiColorMode,
}: IPatternWidget) {
  const { canvasConfig } = useCurrentCanvasSide()
  const { width, height } = canvasConfig
  const [patternCapacity, setPatternCapacity] = useState<{
    rows: number
    columns: number
  }>({ rows: 1, columns: 1 })
  const [coloredSvgData, setColoredSvgData] = useState<string>(assetSvgData)
  const [assetChangeKey, setAssetChangeKey] = useState(0)

  useEffect(() => {
    const coloredSvgData = colorInnerSvgPaths(assetSvgData, color)
    setColoredSvgData(coloredSvgData)
    setAssetChangeKey(assetChangeKey + 1)
  }, [color, assetSvgData])

  const [initialAssetSize, setInitialAssetSize] = useState(50)

  useEffect(() => {
    setInitialAssetSize(
      variant === 'one line pattern'
        ? Math.min(width, height) * 0.5
        : width * EDITOR_CONFIG_FACTOR.ASSET_SIZE_RATIO
    )
  }, [width, height, variant])

  useEffect(() => {
    const { assetWidth, assetHeight } = calculateAssetSize(
      canvasConfig,
      scaleStepCount,
      {
        w: initialAssetSize,
        h: initialAssetSize,
      }
    )

    if (!assetWidth || !assetHeight) {
      return
    }

    const { horizontalMargin, verticalMargin } = getMargins({
      assetWidth: assetWidth,
      assetHeight: assetHeight,
      horizontalMarginStepCount,
      verticalMarginStepCount,
      patternVariant: variant,
      canvasHeight: canvasConfig?.height,
    })

    const colWidth = assetWidth + horizontalMargin
    const rowHeight = assetHeight + verticalMargin

    setPatternCapacity({
      rows: Math.ceil(width / colWidth) + 2,
      columns: Math.ceil(height / rowHeight) + 2,
    })
  }, [
    width,
    height,
    scaleStepCount,
    horizontalMarginStepCount,
    verticalMarginStepCount,
    initialAssetSize,
  ])

  const calculatePatternPieces = () => {
    const { assetWidth, assetHeight } = calculateAssetSize(
      canvasConfig,
      scaleStepCount,
      {
        w: initialAssetSize,
        h: initialAssetSize,
      }
    )

    const { horizontalMargin, verticalMargin } = getMargins({
      assetWidth: assetWidth,
      assetHeight: assetHeight,
      horizontalMarginStepCount,
      verticalMarginStepCount,
      patternVariant: variant,
      canvasHeight: canvasConfig?.height,
    })
    const pieces = []

    const { xStartPosition, yStartPosition } =
      calculatePatternWidgetStartPosition({
        patternWidgetConfig: {
          horizontalPositionStepCount,
          verticalPositionStepCount,
          variant,
        },
        canvasConfig,
        assetSize: {
          w: assetWidth,
          h: assetHeight,
        },
        margins: {
          horizontal: horizontalMargin,
          vertical: verticalMargin,
        },
      })

    for (let row = 0; row < patternCapacity.rows; row++) {
      for (let col = 0; col < patternCapacity.columns; col++) {
        const isUsingOffsetPattern = offset && col % 2

        const xPositionOffset = row * (assetWidth + horizontalMargin)
        const xPosition =
          xStartPosition +
          xPositionOffset +
          (isUsingOffsetPattern ? -(assetWidth + horizontalMargin) / 2 : 0)

        const yPositionOffset = col * (assetHeight + verticalMargin)
        const yPosition = yStartPosition + yPositionOffset
        pieces.push(
          <ArtworkWidget
            key={assetChangeKey + '_' + xPosition + '_' + yPosition + row + col}
            position={{
              x: xPosition,
              y: yPosition,
            }}
            size={{
              w: assetWidth,
              h: assetHeight,
            }}
            rotation={rotationAngle}
            assetSvgData={coloredSvgData}
            color={color}
            multiColorMode={multiColorMode}
            assetUrl={assetUrl || initialMultiColorAssetUrl}
          />
        )
      }
    }
    return pieces
  }

  const patternPieces = calculatePatternPieces()

  useEffect(() => {
    calculatePatternPieces()
  }, [
    width,
    height,
    scaleStepCount,
    horizontalPositionStepCount,
    verticalPositionStepCount,
    horizontalMarginStepCount,
    verticalMarginStepCount,
    initialAssetSize,
  ])

  return <g id="pattern-widget">{patternPieces.map((piece) => piece)}</g>
}
